/*
 * Selima quiz — silnik dopasowania materaca.
 *
 * Różnice względem motywowego assets/quiz.js (który zostaje nietknięty):
 *   1. Wynikiem jest RANKING materacy z kolekcji, nie jeden ręcznie wskazany produkt.
 *   2. Pytania mają role: `scoring` (tagi punktują), `size` i `budget` (twarde filtry).
 *   3. Budżet jest przedziałem domkniętym; „3000+” określa tylko dolną granicę.
 *   4. Cena porównywana z budżetem to najtańszy dostępny wariant w wybranym rozmiarze
 *      (pokrowiec potrafi dołożyć 150-200 zł, więc liczymy "od").
 *   5. Gdy nic nie mieści się w budżecie — komunikat i możliwość zmiany odpowiedzi.
 *   6. Pytania warunkowe: `condition_value` pozwala pominąć np. pytania o partnera.
 *
 * Zero zależności, zero requestów — katalog i karty produktów są wyrenderowane
 * przez Liquid w bloku wyników.
 */
(function () {
  'use strict';

  var ANSWER_EVENT = 'selima-quiz:answer';

  /* Z tagów rodziny "prefix-wartosc" wyciąga same wartości: twardosc-h3 -> h3 */
  function tagSuffixes(tags, prefix) {
    return tags
      .filter(function (tag) { return tag.indexOf(prefix) === 0; })
      .map(function (tag) { return tag.slice(prefix.length); })
      .filter(Boolean);
  }

  function capitalize(text) {
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
  }

  function yearsLabel(count) {
    if (count === 1) return '1 rok';
    var last = count % 10;
    var lastTwo = count % 100;
    if (last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14)) return count + ' lata';
    return count + ' lat';
  }

  function el(tag, className) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    return node;
  }

  function setSelected(option, on) {
    option.classList.toggle('is-selected', on);
    option.setAttribute('aria-pressed', on ? 'true' : 'false');
  }

  function parseList(value) {
    return String(value || '')
      .split(',')
      .map(function (item) { return item.trim().toLowerCase(); })
      .filter(Boolean);
  }

  function fillTemplate(template, values) {
    return String(template || '').replace(/\{(\w+)\}/g, function (match, key) {
      return values[key] == null ? '' : values[key];
    });
  }

  /* Buduje podsumowanie z segmentów rozdzielonych "·", pomijając te,
     których zmienne nie mają wartości (np. brak wybranego budżetu). */
  function fillSegments(template, values) {
    return String(template || '')
      .split('·')
      .map(function (segment) { return segment.trim(); })
      .filter(function (segment) {
        var keys = segment.match(/\{(\w+)\}/g) || [];
        return keys.every(function (key) {
          return values[key.slice(1, -1)];
        });
      })
      .map(function (segment) { return fillTemplate(segment, values); })
      .filter(Boolean)
      .join(' · ');
  }

  if (!customElements.get('selima-quiz')) {
    customElements.define(
      'selima-quiz',
      class SelimaQuiz extends HTMLElement {
        connectedCallback() {
          this._key = 'selima-quiz-' + this.dataset.sectionId;
          this._designMode = this.dataset.designMode === 'true';
          this.slides = Array.from(this.querySelectorAll('selima-quiz-slide'));
          if (!this.slides.length) return;

          this.slides.forEach(function (slide) {
            var type = this._slideType(slide);
            if (type) slide.classList.add('wt-quiz__slide--' + type);
          }, this);

          this.resultsEl = this.querySelector('[data-block-type="results"]');
          this.catalog = this._readCatalog();
          this._money = this._buildFormatter();

          this._progressFill = this.querySelector('.wt-quiz__progress-fill');
          this._progressText = this.querySelector('.wt-quiz__progress-text');

          this._state = { currentIndex: 0, answers: {} };

          var restored = false;
          if (!this._designMode) restored = this._restore();
          if (!restored) this._activate(0, false);

          this.addEventListener(ANSWER_EVENT, this._handleAnswer.bind(this));
          this.addEventListener('click', this._handleClick.bind(this));

          /* W customizerze kliknięcie bloku na liście ma pokazać ten ekran,
             inaczej edytuje się pytanie 7, patrząc na pytanie 1. */
          if (window.Shopify && window.Shopify.designMode) {
            this._onBlockSelect = function (event) {
              var index = this.slides.findIndex(function (slide) { return slide.contains(event.target); });
              if (index >= 0) this._activate(index, false);
            }.bind(this);
            document.addEventListener('shopify:block:select', this._onBlockSelect);
          }

          var newsletterForm = this.querySelector('.wt-quiz__newsletter-form');
          if (newsletterForm) {
            newsletterForm.addEventListener('submit', this._handleNewsletterSubmit.bind(this));
          }
        }

        disconnectedCallback() {
          if (this._onBlockSelect) document.removeEventListener('shopify:block:select', this._onBlockSelect);
        }

        /* ------------------------------------------------------------------ */
        /* dane                                                                */
        /* ------------------------------------------------------------------ */

        _readCatalog() {
          var node = this.querySelector('[data-quiz-catalog]');
          if (!node) return [];
          var raw;
          try {
            raw = JSON.parse(node.textContent);
          } catch (error) {
            return [];
          }
          if (!Array.isArray(raw)) return [];

          return raw
            .map(function (item) {
              /* bez prototypu — rozmiar jest kluczem obiektu i nie może trafić
                 na "constructor" czy "toString" */
              var bySize = Object.create(null);
              (item.v || []).forEach(function (variant) {
                var size = variant[0];
                var price = variant[1];
                /* pusty rozmiar = produkt nie ma opcji wariantu z rozmiarem;
                   nie da się go dopasować ani wycenić per rozmiar */
                if (!size) return;
                if (bySize[size] == null || price < bySize[size]) bySize[size] = price;
              });
              return {
                handle: item.h,
                title: item.t,
                url: item.u,
                image: item.i || '',
                tags: (item.tags || []).map(function (tag) { return String(tag).toLowerCase(); }),
                bySize: bySize,
              };
            })
            .filter(function (product) { return Object.keys(product.bySize).length > 0; });
        }

        _buildFormatter() {
          var currency = this.dataset.currency || 'PLN';
          var locale = this.dataset.locale || 'pl';
          var formatter;
          try {
            formatter = new Intl.NumberFormat(locale, {
              style: 'currency',
              currency: currency,
              maximumFractionDigits: 0,
            });
          } catch (error) {
            formatter = null;
          }
          return function (cents) {
            var amount = Math.round(cents / 100);
            return formatter ? formatter.format(amount) : amount + ' ' + currency;
          };
        }

        /* ------------------------------------------------------------------ */
        /* slajdy i warunki                                                    */
        /* ------------------------------------------------------------------ */

        _slideType(slide) {
          if (!slide) return null;
          var node = slide.querySelector('[data-block-type]');
          return node ? node.dataset.blockType : null;
        }

        _questionEl(slide) {
          return slide ? slide.querySelector('[data-block-type="question"]') : null;
        }

        _isQuestion(slide) {
          return !!slide && this._slideType(slide) === 'question';
        }

        /* Slajd jest widoczny, jeśli nie ma warunku albo któraś z dotychczasowych
           odpowiedzi ma wartość wymienioną w `condition_value`. */
        _isVisible(slide) {
          var question = this._questionEl(slide);
          if (!question) return true;
          var wanted = parseList(question.dataset.conditionValue);
          if (!wanted.length) return true;
          var chosen = Object.keys(this._state.answers).map(function (id) {
            return (this._state.answers[id].value || '').toLowerCase();
          }, this);
          return wanted.some(function (value) { return chosen.indexOf(value) !== -1; });
        }

        /* Gdy klient cofnie się i zmieni odpowiedź warunkującą (np. "dla pary"
           na "dla siebie"), odpowiedzi ukrytych pytań muszą zniknąć — inaczej
           waga partnera nadal filtrowałaby wyniki. */
        _pruneHiddenAnswers() {
          for (var pass = 0; pass < 5; pass++) {
            var changed = false;
            this.slides.forEach(function (slide) {
              var question = this._questionEl(slide);
              if (!question) return;
              var id = question.dataset.blockId;
              if (this._state.answers[id] && !this._isVisible(slide)) {
                delete this._state.answers[id];
                slide.querySelectorAll('selima-quiz-option').forEach(function (option) {
                  setSelected(option, false);
                });
                changed = true;
              }
            }, this);
            if (!changed) break;
          }
        }

        _visibleQuestionSlides() {
          return this.slides.filter(function (slide) {
            return this._isQuestion(slide) && this._isVisible(slide);
          }, this);
        }

        _resultsIndex() {
          return this.slides.findIndex(function (slide) {
            return this._slideType(slide) === 'results';
          }, this);
        }

        /* ------------------------------------------------------------------ */
        /* nawigacja                                                           */
        /* ------------------------------------------------------------------ */

        _activate(index, animate) {
          this.slides.forEach(function (slide, i) {
            slide.classList.toggle('is-active', i === index);
          });
          this._state.currentIndex = index;
          this._syncProgress(index);
          this._syncNav(index);

          if (animate !== false) {
            this._scrollIntoView();
            var firstOption = this.slides[index] && this.slides[index].querySelector('selima-quiz-option');
            if (firstOption) requestAnimationFrame(function () { firstOption.focus({ preventScroll: true }); });
          }
        }

        /* Wysokość przyklejonego nagłówka motywu — żeby nie przewinąć pod niego. */
        _stickyOffset() {
          var header = document.querySelector('.page-header');
          if (!header) return 0;
          var position = getComputedStyle(header).position;
          if (position !== 'sticky' && position !== 'fixed') return 0;
          return Math.round(header.getBoundingClientRect().height);
        }

        /* Ekran wyników jest długi (karty + tabela), więc po „rozwiąż ponownie”
           albo po przejściu dalej klient zostawał na dole strony, patrząc na pustkę.
           Przewijamy tylko wtedy, gdy góra quizu wyjechała ponad widok — przy
           krótkim quizie na desktopie nic się nie dzieje.

           Zmiana slajdu potrafi skrócić stronę o kilka tysięcy pikseli i przeglądarka
           przycina wtedy pozycję przewijania, więc dociągamy jeszcze raz, gdy układ
           się ustabilizuje. setTimeout zamiast rAF, bo klatki nie lecą, kiedy karta
           jest w tle. */
        _scrollIntoView() {
          var self = this;
          this._scrollToQuizTop('smooth');
          setTimeout(function () { self._scrollToQuizTop('smooth'); }, 0);
          setTimeout(function () { self._scrollToQuizTop('auto', 40); }, 350);
        }

        _scrollToQuizTop(behavior, tolerance) {
          var offset = this._stickyOffset();
          var rect = this.getBoundingClientRect();
          var slack = tolerance || 1;
          if (rect.top >= offset - slack) return;

          var current = window.pageYOffset || document.documentElement.scrollTop || 0;
          var top = Math.max(0, Math.round(rect.top + current - offset - 12));
          var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          var smooth = behavior === 'smooth' && !reduced &&
            'scrollBehavior' in document.documentElement.style;

          /* Dwuargumentowa forma działa wszędzie i domyka pozycję nawet wtedy,
             gdy płynne przewijanie jest wyłączone albo ignorowane. */
          if (!smooth) {
            window.scrollTo(0, top);
            return;
          }
          try {
            window.scrollTo({ top: top, behavior: 'smooth' });
          } catch (error) {
            window.scrollTo(0, top);
          }
        }

        _advance() {
          var next = this._state.currentIndex + 1;
          while (next < this.slides.length && !this._isVisible(this.slides[next])) next++;
          if (next >= this.slides.length) return;

          if (next === this._resultsIndex()) {
            this._compute();
            this._state.completedAt = new Date().toISOString();
            this._save();
          }
          this._activate(next);
        }

        _back() {
          var prev = this._state.currentIndex - 1;
          while (prev >= 0 && !this._isVisible(this.slides[prev])) prev--;
          if (prev >= 0) this._activate(prev);
        }

        _syncNav(index) {
          var slide = this.slides[index];
          if (!slide) return;
          var nextBtn = slide.querySelector('.wt-quiz__nav-next');
          if (!nextBtn) return;

          nextBtn.disabled = !slide.querySelector('selima-quiz-option.is-selected');

          var questionSlides = this._visibleQuestionSlides();
          var position = questionSlides.indexOf(slide);
          if (position === -1) return;

          var prevBtn = slide.querySelector('.wt-quiz__nav-prev');
          if (prevBtn) prevBtn.hidden = position === 0;

          var label = nextBtn.querySelector('.wt-quiz__nav-label');
          if (label) {
            var isLast = position === questionSlides.length - 1;
            label.textContent = isLast
              ? nextBtn.dataset.resultLabel || label.textContent
              : nextBtn.dataset.nextLabel || label.textContent;
          }
        }

        _syncProgress(index) {
          var slide = this.slides[index];
          var isQuestion = this._isQuestion(slide);
          var progressEl = this.querySelector('.wt-quiz__progress');
          if (progressEl) progressEl.hidden = !isQuestion;
          if (!isQuestion) return;

          var headerEl = this.querySelector('.wt-quiz__header');
          if (headerEl) {
            var styles = getComputedStyle(slide);
            headerEl.style.backgroundColor = styles.getPropertyValue('--quiz-block-bg').trim() || '';
            headerEl.style.color = styles.getPropertyValue('--quiz-block-text').trim() || '';
          }

          var questionSlides = this._visibleQuestionSlides();
          var position = questionSlides.indexOf(slide) + 1;
          var total = questionSlides.length;
          if (this._progressFill) this._progressFill.style.width = (position / total) * 100 + '%';
          if (this._progressText) this._progressText.textContent = position + ' / ' + total;
        }

        /* ------------------------------------------------------------------ */
        /* zdarzenia                                                           */
        /* ------------------------------------------------------------------ */

        _handleClick(event) {
          if (event.target.closest('.wt-quiz__nav-next:not([disabled])')) {
            this._advance();
            return;
          }
          if (event.target.closest('.wt-quiz__nav-prev')) {
            this._back();
            return;
          }
          if (event.target.closest('.wt-quiz__retake')) {
            event.preventDefault();
            this._retake();
            return;
          }
          if (event.target.closest('.wt-quiz__newsletter-skip')) {
            event.preventDefault();
            this._advance();
            return;
          }
          var newsletterSubmit = event.target.closest('.wt-newsletter__send');
          if (newsletterSubmit) {
            event.preventDefault();
            var form = newsletterSubmit.closest('form');
            if (form) form.requestSubmit();
            return;
          }
          /* CTA z ekranu intro (motywowy blok renderuje je jako <a href="#">).
             Przyciski pytań i wyników są obsłużone wyżej, a link kontaktowy pod
             wynikami zostaje linkiem — dlatego decyduje typ slajdu, nie tag. */
          var heroButton = event.target.closest('.hero__button');
          if (heroButton) {
            var slide = heroButton.closest('selima-quiz-slide');
            var type = slide ? this._slideType(slide) : null;
            if (type && type !== 'question' && type !== 'results') {
              event.preventDefault();
              this._advance();
            }
          }
        }

        _handleAnswer(event) {
          var option = event.detail.option;
          var slide = option.closest('selima-quiz-slide');
          var question = slide && this._questionEl(slide);
          if (!question) return;

          this._state.answers[question.dataset.blockId] = this._answerFrom(question, option);

          this._pruneHiddenAnswers();
          this._save();
          this._syncNav(this._state.currentIndex);
          this._syncProgress(this._state.currentIndex);
        }

        _answerFrom(question, option) {
          return {
            optionId: option.dataset.blockId,
            value: (option.dataset.value || '').trim(),
            tags: parseList(option.dataset.tags),
            required: option.dataset.tagsRequired === 'true',
            role: question.dataset.role || 'scoring',
            weight: Number(question.dataset.weight) || 1,
          };
        }

        _handleNewsletterSubmit(event) {
          event.preventDefault();
          var form = event.target;
          fetch(form.action, {
            method: 'POST',
            body: new FormData(form),
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            redirect: 'manual',
          })
            .catch(function () {})
            .finally(this._advance.bind(this));
        }

        _retake() {
          this._returnCardsToPool();
          this._state = { currentIndex: 0, answers: {} };
          try { localStorage.removeItem(this._key); } catch (error) {}
          this.querySelectorAll('selima-quiz-option').forEach(function (option) {
            setSelected(option, false);
          });
          if (this._progressFill) {
            this._progressFill.style.transition = 'none';
            this._progressFill.style.width = '0%';
            requestAnimationFrame(function () { this._progressFill.style.transition = ''; }.bind(this));
          }
          var firstQuestion = this.slides.findIndex(function (slide) { return this._isQuestion(slide); }, this);
          this._activate(firstQuestion >= 0 ? firstQuestion : 0);
        }

        /* ------------------------------------------------------------------ */
        /* dopasowanie                                                         */
        /* ------------------------------------------------------------------ */

        /* Kwoty w zł: „2000” / „do 2000”, „1500–2000”, „3000+”.
           Wynik w groszach, zgodnie z cenami wariantów Shopify. */
        _parseBudget(value) {
          if (value === 'no_preference') return { min: 200000, max: null };
          var text = String(value || '').replace(/[\s\u00a0]/g, '');
          text = text.replace(/(\d)[.,](?=\d{3}(?:\D|$))/g, '$1');
          var groups = text.match(/\d+(?:[.,]\d{1,2})?/g);
          if (!groups) return { min: null, max: null };
          var amounts = groups.map(function (group) {
            return Math.round(Number(group.replace(',', '.')) * 100);
          });
          if (amounts.length > 1) {
            return { min: Math.min.apply(null, amounts), max: Math.max.apply(null, amounts) };
          }
          if (text.indexOf('+') !== -1 || /^od/i.test(text)) {
            return { min: amounts[0], max: null };
          }
          return { min: null, max: amounts[0] };
        }

        _compute() {
          var results = this.resultsEl;
          if (!results) return;

          var answers = Object.keys(this._state.answers).map(function (id) {
            return this._state.answers[id];
          }, this);

          var sizeAnswer = answers.find(function (a) { return a.role === 'size'; });
          var size = sizeAnswer && sizeAnswer.value ? sizeAnswer.value : null;

          var budgetAnswer = answers.find(function (a) { return a.role === 'budget'; });
          var budget = this._parseBudget(budgetAnswer && budgetAnswer.value);

          var required = [];
          var scoring = [];
          answers
            .filter(function (a) { return a.role === 'scoring'; })
            .forEach(function (answer) {
              answer.tags.forEach(function (tag) {
                (answer.required ? required : scoring).push({ tag: tag, weight: answer.weight });
              });
            });

          /* twardy filtr 1 — rozmiar musi istnieć i być dostępny */
          var pool = this.catalog.filter(function (product) {
            return size ? product.bySize[size] != null : Object.keys(product.bySize).length > 0;
          });

          /* twardy filtr 2 — tagi wymagane (np. udźwig wagi). Gdyby wyzerowały
             wynik, odpuszczamy je i mówimy o tym wprost. */
          var relaxed = false;
          if (required.length) {
            var strict = pool.filter(function (product) {
              return required.every(function (item) { return product.tags.indexOf(item.tag) !== -1; });
            });
            if (strict.length) {
              pool = strict;
            } else {
              relaxed = true;
            }
          }

          var rows = pool
            .map(function (product) {
              var rowSize = size;
              var price;
              if (size) {
                price = product.bySize[size];
              } else {
                /* Klient nie podał rozmiaru — bierzemy najtańszy dostępny i mówimy,
                   którego rozmiaru cena dotyczy, zamiast zostawiać puste miejsce. */
                Object.keys(product.bySize).forEach(function (key) {
                  if (price == null || product.bySize[key] < price) {
                    price = product.bySize[key];
                    rowSize = key;
                  }
                });
              }
              var score = scoring.reduce(function (total, item) {
                return total + (product.tags.indexOf(item.tag) !== -1 ? item.weight : 0);
              }, 0);
              return { product: product, price: price, size: rowSize, score: score };
            })
            .filter(function (row) { return row.price != null && isFinite(row.price); });

          this._render(rows, { size: size, budgetMin: budget.min, budgetMax: budget.max, budgetNoPreference: budgetAnswer && budgetAnswer.value === 'no_preference', relaxed: relaxed });
        }

        _render(rows, context) {
          var results = this.resultsEl;
          var data = results.dataset;
          var money = this._money;
          var size = context.size;
          var budgetMax = context.budgetMax;
          var budgetMin = context.budgetMin;
          var limit = Number(data.resultsToShow) || 3;

          var summaryEl = results.querySelector('[data-quiz-summary]');
          var noticeEl = results.querySelector('[data-quiz-notice]');
          var fallbackEl = results.querySelector('[data-quiz-fallback]');

          this._returnCardsToPool();

          if (summaryEl) {
            var budgetLabel = budgetMin != null
              ? (budgetMax != null ? money(budgetMin) + '–' + money(budgetMax) : 'od ' + money(budgetMin))
              : (budgetMax != null ? 'do ' + money(budgetMax) : '');
            if (context.budgetNoPreference) budgetLabel = 'bez znaczenia';
            /* Obsługa także starszego szablonu zapisanego w customizerze. */
            var summary = fillSegments(String(data.tplSummary || '').replace(/do\s+\{budget\}/g, '{budget}'), {
              size: size || '',
              budget: budgetLabel,
            });
            summaryEl.textContent = summary;
            summaryEl.hidden = !summary;
          }

          var notices = [];
          if (context.relaxed && data.tplRelaxed) notices.push(data.tplRelaxed);

          var inBudget = rows.filter(function (row) {
            return (budgetMin == null || row.price >= budgetMin) &&
              (budgetMax == null || row.price <= budgetMax);
          });
          if (!inBudget.length) {
            if (rows.length) {
              notices.push(data.tplNoBudgetMatch || 'Żaden dopasowany materac nie mieści się w wybranym budżecie. Zmień budżet lub rozmiar.');
            }
            this._renderNotices(noticeEl, notices);
            if (fallbackEl) fallbackEl.hidden = false;
            this._renderComparison([], {});
            return;
          }
          if (fallbackEl) fallbackEl.hidden = true;

          var byMatch = function (a, b) {
            return b.score - a.score || a.price - b.price || a.product.title.localeCompare(b.product.title);
          };
          var cards = inBudget.sort(byMatch).slice(0, limit);
          this._renderNotices(noticeEl, notices);

          /* Etykietę „najlepiej dopasowany" dostaje najlepszy z POKAZANYCH modeli —
             globalny lider mógł odpaść na budżecie i wtedy etykieta znikała bez
             wytłumaczenia. Nie oznaczamy nic, gdy wszystkie karty mają równy wynik. */
          var displayedBest = cards.slice().sort(byMatch)[0];
          var hasSpread = cards.some(function (row) { return row.score < displayedBest.score; });

          this._placeCards(cards, {
            size: size,
            budgetMax: budgetMax,
            bestRow: hasSpread ? displayedBest : null,
          });

          this._renderComparison(cards, { size: size });
        }

        /* Komunikaty wstawiamy jako tekst — wchodzą do nich tytuły produktów
           ze sklepu, więc nie chcemy ich traktować jako HTML. */
        _renderNotices(noticeEl, notices) {
          if (!noticeEl) return;
          noticeEl.textContent = '';
          notices.forEach(function (text) {
            if (!text) return;
            var p = document.createElement('p');
            p.textContent = text;
            noticeEl.appendChild(p);
          });
          noticeEl.hidden = !noticeEl.childNodes.length;
        }

        /* Największy rozmiar tego modelu, który mieści się w budżecie. */
        _alternativeSize(product, currentSize, budgetMax) {
          if (!budgetMax) return null;
          var options = Object.keys(product.bySize)
            .filter(function (size) { return size !== currentSize && product.bySize[size] <= budgetMax; })
            .map(function (size) { return { size: size, price: product.bySize[size] }; })
            .sort(function (a, b) { return b.price - a.price; });
          return options[0] || null;
        }

        _cardNode(handle) {
          return this.querySelector('[data-quiz-card="' + String(handle).replace(/"/g, '\\"') + '"]');
        }

        _returnCardsToPool() {
          var pool = this.querySelector('[data-quiz-pool]');
          var grid = this.resultsEl && this.resultsEl.querySelector('[data-quiz-cards]');
          if (!pool || !grid) return;
          grid.querySelectorAll('[data-quiz-card]').forEach(function (card) {
            var badges = card.querySelector('[data-card-badges]');
            var price = card.querySelector('[data-card-price]');
            if (badges) badges.innerHTML = '';
            if (price) { price.textContent = ''; price.hidden = true; }
            pool.appendChild(card);
          });
        }

        _placeCards(rows, context) {
          var grid = this.resultsEl.querySelector('[data-quiz-cards]');
          if (!grid) return;
          var data = this.resultsEl.dataset;
          var money = this._money;

          rows.forEach(function (row) {
            var card = this._cardNode(row.product.handle);
            if (!card) return;

            var badges = card.querySelector('[data-card-badges]');
            if (badges) {
              badges.innerHTML = '';
              if (rows.length > 1 && row === context.bestRow && data.tplBadgeBest) {
                badges.appendChild(this._badge(data.tplBadgeBest, 'best'));
              }
              if (context.budgetMax && row.price > context.budgetMax && data.tplBadgeOver) {
                badges.appendChild(
                  this._badge(
                    fillTemplate(data.tplBadgeOver, { delta: money(row.price - context.budgetMax) }),
                    'over'
                  )
                );
              }
            }

            var priceEl = card.querySelector('[data-card-price]');
            if (priceEl && data.tplPrice) {
              priceEl.textContent = fillTemplate(data.tplPrice, {
                price: money(row.price),
                size: row.size || context.size || '',
              });
              priceEl.hidden = false;
            }

            grid.appendChild(card);
          }, this);
        }

        /* ------------------------------------------------------------------ */
        /* tabela porównawcza                                                  */
        /* ------------------------------------------------------------------ */

        /* Wiersze budujemy z tagów, które produkty już mają w Shopify — nie ma
           tu ani jednego pola do uzupełnienia. Wiersz, którego nie ma żaden
           z pokazanych materacy, wypada, żeby tabela nie świeciła pustkami. */
        _comparisonRows(cards, context) {
          var data = this.resultsEl.dataset;
          var money = this._money;
          var rows = [];

          function addValueRow(label, getValue) {
            if (!label) return;
            var values = cards.map(function (row) { return getValue(row) || ''; });
            if (!values.some(Boolean)) return;
            rows.push({ label: label, values: values.map(function (text) { return { text: text }; }) });
          }

          addValueRow(data.cmpPrice, function (row) { return money(row.price); });

          addValueRow(data.cmpType, function (row) {
            return tagSuffixes(row.product.tags, 'typ-').map(capitalize).join(' / ');
          });

          addValueRow(data.cmpFirmness, function (row) {
            return tagSuffixes(row.product.tags, 'twardosc-')
              .map(function (value) { return value.toUpperCase(); })
              .sort()
              .join(' / ');
          });

          addValueRow(data.cmpWeight, function (row) {
            var weights = tagSuffixes(row.product.tags, 'waga-')
              .map(Number)
              .filter(function (value) { return !isNaN(value); })
              .sort(function (a, b) { return a - b; });
            if (!weights.length) return '';
            var min = weights[0];
            var max = weights[weights.length - 1];
            return (min === max ? String(max) : min + '–' + max) + ' kg';
          });

          addValueRow(data.cmpWarranty, function (row) {
            var years = tagSuffixes(row.product.tags, 'gwarancja-')
              .map(function (value) { return parseInt(value, 10); })
              .filter(function (value) { return !isNaN(value); })
              .sort(function (a, b) { return b - a; });
            return years.length ? yearsLabel(years[0]) : '';
          });

          /* wiersze ✓ / — z ustawienia "Etykieta = tag, tag2" */
          String(data.cmpRows || '')
            .split(/\r?\n/)
            .forEach(function (line) {
              var parts = line.split('=');
              if (parts.length < 2) return;
              var label = parts[0].trim();
              var tags = parseList(parts.slice(1).join('='));
              if (!label || !tags.length) return;
              var values = cards.map(function (row) {
                return tags.some(function (tag) { return row.product.tags.indexOf(tag) !== -1; });
              });
              if (!values.some(Boolean)) return;
              rows.push({ label: label, values: values.map(function (has) { return { check: has }; }) });
            });

          return rows;
        }

        _renderComparison(cards, context) {
          var wrap = this.resultsEl.querySelector('[data-quiz-comparison]');
          if (!wrap) return;
          var mount = wrap.querySelector('[data-comparison-mount]');
          if (!mount) return;

          mount.textContent = '';
          /* porównanie jednego materaca z niczym nie ma sensu */
          var rows = cards.length > 1 ? this._comparisonRows(cards, context) : [];
          if (!rows.length) {
            wrap.hidden = true;
            return;
          }
          /* liczba kolumn steruje minimalną szerokością tabeli na telefonie */
          wrap.style.setProperty('--cmp-cols', cards.length);

          var table = el('table', 'wt-comparison-table__table');

          var thead = document.createElement('thead');
          var headRow = el('tr', 'wt-comparison-table__row');
          var corner = el('th', 'wt-comparison-table__header-label');
          corner.setAttribute('scope', 'col');
          headRow.appendChild(corner);

          cards.forEach(function (row, index) {
            var th = el('th', 'wt-comparison-table__column' + (index === 0 ? ' wt-comparison-table__column--first' : ''));
            th.setAttribute('scope', 'col');
            if (row.product.image) {
              var imageBox = el('div', 'wt-comparison-table__column-product-image');
              var image = document.createElement('img');
              image.src = row.product.image;
              image.alt = '';
              image.loading = 'lazy';
              imageBox.appendChild(image);
              th.appendChild(imageBox);
            }
            /* Motywowa sekcja trzyma tu richtext, czyli <p> — CSS ma pod to regułę
               .wt-comparison-table__column-heading p, więc zachowujemy tę strukturę. */
            var heading = el('div', 'wt-comparison-table__column-heading headline headline--rich');
            var paragraph = document.createElement('p');
            var link = document.createElement('a');
            link.href = row.product.url || '#';
            link.textContent = row.product.title;
            paragraph.appendChild(link);
            heading.appendChild(paragraph);
            th.appendChild(heading);
            headRow.appendChild(th);
          });

          thead.appendChild(headRow);
          table.appendChild(thead);

          var checkTemplate = this.querySelector('[data-quiz-check-icon]');
          var tbody = document.createElement('tbody');

          rows.forEach(function (row) {
            var tr = el('tr', 'wt-comparison-table__row wt-comparison-table__row-border');
            var name = el('th', 'wt-comparison-table__feature-name');
            name.setAttribute('scope', 'row');
            name.textContent = row.label;
            tr.appendChild(name);

            row.values.forEach(function (value, index) {
              var td = el('td', 'wt-comparison-table__feature-value' + (index === 0 ? ' wt-comparison-table__feature-value--first' : ''));
              if (value.check === true) {
                var icon = el('span', 'wt-comparison-table__feature-icon');
                if (checkTemplate && checkTemplate.content) {
                  icon.appendChild(checkTemplate.content.cloneNode(true));
                } else {
                  icon.textContent = '✓';
                }
                td.appendChild(icon);
                td.setAttribute('aria-label', 'tak');
              } else if (value.check === false) {
                td.textContent = '—';
                td.setAttribute('aria-label', 'nie');
              } else {
                td.textContent = value.text || '—';
              }
              tr.appendChild(td);
            });

            tbody.appendChild(tr);
          });

          table.appendChild(tbody);
          mount.appendChild(table);
          wrap.hidden = false;
        }

        _badge(text, modifier) {
          var span = document.createElement('span');
          span.className = 'selima-quiz__badge selima-quiz__badge--' + modifier;
          span.textContent = text;
          return span;
        }

        /* ------------------------------------------------------------------ */
        /* trwałość                                                            */
        /* ------------------------------------------------------------------ */

        _save() {
          try {
            localStorage.setItem(this._key, JSON.stringify(this._state));
          } catch (error) {}
        }

        _restore() {
          var saved;
          try {
            saved = JSON.parse(localStorage.getItem(this._key));
          } catch (error) {
            return false;
          }
          if (!saved || !saved.completedAt || !saved.answers ||
              typeof saved.answers !== 'object' || Array.isArray(saved.answers)) return false;

          var resultsIndex = this._resultsIndex();
          if (resultsIndex < 0) return false;

          /* Odtwarzamy WYBORY (id odpowiedzi), a tagi, wagi i role czytamy na nowo
             z bieżącego DOM. Dzięki temu zmiana tagów w customizerze działa też dla
             wracającego klienta, a usunięte pytanie po prostu wypada ze stanu —
             zamiast po cichu filtrować wyniki starą konfiguracją. */
          var answers = {};
          var selected = [];
          Object.keys(saved.answers).forEach(function (questionId) {
            var savedAnswer = saved.answers[questionId];
            var optionId = savedAnswer && savedAnswer.optionId;
            if (typeof optionId !== 'string' || !optionId) return;
            var option = Array.from(this.querySelectorAll('selima-quiz-option')).find(function (candidate) {
              return candidate.dataset.blockId === optionId;
            });
            if (!option) return;
            var slide = option.closest('selima-quiz-slide');
            var question = this._questionEl(slide);
            if (!question || question.dataset.blockId !== questionId) return;
            answers[questionId] = this._answerFrom(question, option);
            selected.push(option);
          }, this);

          if (!Object.keys(answers).length) return false;

          var previousState = this._state;
          this._state = { currentIndex: resultsIndex, answers: answers, completedAt: saved.completedAt };
          this._pruneHiddenAnswers();
          /* Nowe/usunięte pytania lub uszkodzony zapis nie mogą omijać budżetu
             ani innych widocznych pytań. W takim przypadku zaczynamy od nowa. */
          var complete = this._visibleQuestionSlides().every(function (slide) {
            return !!this._state.answers[this._questionEl(slide).dataset.blockId];
          }, this);
          if (!complete) {
            this._state = previousState;
            return false;
          }
          selected.forEach(function (option) {
            var question = this._questionEl(option.closest('selima-quiz-slide'));
            if (this._state.answers[question.dataset.blockId]) setSelected(option, true);
          }, this);

          this._compute();
          this._activate(resultsIndex, false);
          return true;
        }
      }
    );
  }

  if (!customElements.get('selima-quiz-slide')) {
    customElements.define('selima-quiz-slide', class SelimaQuizSlide extends HTMLElement {});
  }

  if (!customElements.get('selima-quiz-option')) {
    customElements.define(
      'selima-quiz-option',
      class SelimaQuizOption extends HTMLElement {
        connectedCallback() {
          if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');
          if (!this.hasAttribute('role')) this.setAttribute('role', 'button');
          this.setAttribute('aria-pressed', this.classList.contains('is-selected') ? 'true' : 'false');
          this._onClick = this._select.bind(this);
          this._onKeydown = function (event) {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              this._select();
            }
          }.bind(this);
          this.addEventListener('click', this._onClick);
          this.addEventListener('keydown', this._onKeydown);
        }

        disconnectedCallback() {
          this.removeEventListener('click', this._onClick);
          this.removeEventListener('keydown', this._onKeydown);
        }

        _select() {
          if (this.classList.contains('is-selected')) return;
          var group = this.closest('.wt-quiz__options');
          if (group) {
            group.querySelectorAll('selima-quiz-option').forEach(function (option) {
              setSelected(option, false);
            });
          }
          setSelected(this, true);
          this.dispatchEvent(new CustomEvent(ANSWER_EVENT, { bubbles: true, detail: { option: this } }));
        }
      }
    );
  }
})();
